// src/pages/offlineSales/components/OfflineSalesOrderDetailsModal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Package, User, Eye, ExternalLink, X, Pencil, Truck, FileText, RotateCcw, CreditCard, Calendar } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAlert } from '../../../contexts/AlertContext';
import { getOrder } from '../../../services/api/offlineSales/orders';
import { formatDate } from '../../../utils/dateUtils';
import { PaymentMethodIcon } from '../../../utils/paymentMethodIcons';
import styles from './OfflineSalesOrderDetailsModal.module.css';

const STATUS_COLORS = {
  offline_completed: 'success', offline_voided: 'error', offline_on_hold: 'warning',
  offline_credit: 'info', offline_returned: 'default', offline_partially_returned: 'warning',
  offline_draft: 'default',
  pending: 'warning', confirmed: 'info', hold: 'warning', not_confirmed: 'warning',
  cancelled: 'error', in_packing: 'info', in_courier: 'info', settled: 'success', returned: 'default',
};
const PAYMENT_COLORS = { paid: 'success', partial: 'warning', unpaid: 'error' };

const Badge = ({ type, label }) => (
  <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{label}</span>
);

const OfflineSalesOrderDetailsModal = ({ order: rowOrder, onClose }) => {
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { hasPermission } = usePermissions();
  const { error } = useAlert();
  const [order, setOrder] = useState(rowOrder);
  const [loadingItems, setLoadingItems] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getOrder(rowOrder.id);
        if (!cancelled) setOrder(result.order || result);
      } catch (err) {
        error(err?.response?.data?.message || 'Failed to load order details');
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    })();
    return () => { cancelled = true; };
  }, [rowOrder.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleViewOrder = () => {
    navigate(`/offline-sales/orders/${order.id}`);
    onClose();
  };

  const handleEditOrder = () => {
    navigate(`/offline-sales/orders/${order.id}/edit`);
    onClose();
  };

  const statusKey = order.OrderStatus?.key;
  const canEdit = !order.OrderStatus?.isClosed && hasPermission('edit_offline_sales_order');

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Package size={22} />
              <div>
                <h2>{t('offlineSales.offlineSalesOrders')}</h2>
                <button
                  className={styles.orderNumberLink}
                  onClick={handleViewOrder}
                  title={t('offlineSales.viewOrderDetails', 'Click to view order details')}
                >
                  #{order.orderNumber}
                </button>
              </div>
            </div>
            <button onClick={onClose} className={styles.closeButton}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div className={styles.modalBody}>
          {/* Order Details */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>{t('offlineSales.orderNumber')}</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>{t('offlineSales.statusCol')}</label>
                <p><Badge type={STATUS_COLORS[statusKey] || 'default'} label={t(`offlineSales.status_${statusKey}`, order.OrderStatus?.label)} /></p>
              </div>
              <div className={styles.infoItem}>
                <label>{t('offlineSales.paymentStatusCol')}</label>
                <p><Badge type={PAYMENT_COLORS[order.paymentStatus] || 'default'} label={t(`offlineSales.payment_${order.paymentStatus}`, order.paymentStatus)} /></p>
              </div>
              <div className={styles.infoItem}>
                <label>{t('offlineSales.date')}</label>
                <p>{new Date(order.createdAt).toLocaleString()}</p>
              </div>
              <div className={styles.infoItem}>
                <label>{t(order.channel === 'pos' ? 'offlineSales.cashier' : 'ecomOrders.createdBy')}</label>
                <p>{order.cashier?.name || '—'}</p>
              </div>
              {order.channel === 'offline' && (
                <div className={styles.infoItem}>
                  <label>{t('offlineSales.salesRep')}</label>
                  <p>{order.salesRep?.name || '—'}</p>
                </div>
              )}
            </div>
          </div>

          {/* Customer Details */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}><User size={15} /> {t('offlineSales.customer')}</h3>
            {order.customer ? (
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <label>{t('offlineSales.customerName')}</label>
                  <p>{order.customer.name}</p>
                </div>
                {order.customer.phone && (
                  <div className={styles.infoItem}>
                    <label>{t('offlineSales.customerPhone')}</label>
                    <p>{order.customer.phone}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className={styles.dim}>{t('offlineSales.cashSales')}</p>
            )}
          </div>

          {/* Items */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>{t('offlineSales.items')}</h3>
            {loadingItems ? (
              <p className={styles.dim}>{t('common.loading', 'Loading...')}</p>
            ) : (
              (order.orderItems || []).map((item) => (
                <div key={item.id} className={styles.itemRow}>
                  <span>
                    {item.Product?.name || item.displayName || 'Item'} × {item.quantity}
                    {item.freeQuantity > 0 && ` (${t('offlineSales.itemsIncludedFree', { count: item.freeQuantity })})`}
                  </span>
                  <span>{formatCurrency(item.lineTotal)}</span>
                </div>
              ))
            )}
          </div>

          {/* Delivery Notes Card */}
          {(order.deliveryNoteDocuments || []).length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}><Truck size={15} /> {t('offlineSales.deliveryNotes', 'Delivery Notes')}</h3>
              {order.deliveryNoteDocuments.map((dn) => (
                <div key={dn.id} className={styles.infoRow}>
                  <span><Link to={`/offline-sales/delivery-notes/${dn.id}`} onClick={onClose} className={styles.noteLink}><strong>{dn.deliveryNoteNumber}</strong></Link> · {formatDate(dn.deliveredAt)}</span>
                  <Badge type="default" label={dn.status || 'delivered'} />
                </div>
              ))}
            </div>
          )}

          {/* Credit Notes Card */}
          {(order.creditNotes || []).length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}><FileText size={15} /> {t('offlineSales.creditNotes', 'Credit Notes')}</h3>
              {order.creditNotes.map((cn) => (
                <div key={cn.id} className={styles.infoRow}>
                  <span><Link to={`/offline-sales/credit-notes/${cn.id}`} onClick={onClose} className={styles.noteLink}><strong>{cn.creditNoteNumber}</strong></Link> ({formatCurrency(cn.appliedAmount)} / {formatCurrency(cn.amount)})</span>
                  <Badge type="default" label={cn.status || 'active'} />
                </div>
              ))}
            </div>
          )}

          {/* Returns Card */}
          {(order.returns || []).length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}><RotateCcw size={15} /> {t('offlineSales.returns', 'Return Notes')}</h3>
              {order.returns.map((ret) => (
                <div key={ret.id} className={styles.infoRow}>
                  <span><Link to={`/offline-sales/return-notes/${ret.id}`} onClick={onClose} className={styles.noteLink}><strong>{ret.returnNumber}</strong></Link> · {formatCurrency(ret.totalRefundAmount)}</span>
                  <Badge type="warning" label={ret.status || 'returned'} />
                </div>
              ))}
            </div>
          )}

          {/* Payment Records */}
          {(order.paymentRecords || []).length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}><CreditCard size={15} /> {t('offlineSales.payments')}</h3>
              <div className={styles.paymentList}>
                {order.paymentRecords.map((p) => (
                  <div key={p.id} className={styles.paymentRecord}>
                    <div className={styles.paymentHeader}>
                      <PaymentMethodIcon iconFileName={p.PaymentMethod?.iconFileName} label={p.PaymentMethod?.label} />
                      <span className={styles.paymentAmount}>{formatCurrency(p.paymentAmount)}</span>
                    </div>
                    {p.reference && <div className={styles.paymentReference}>Ref: {p.reference}</div>}
                    <div className={styles.paymentDate}>
                      <Calendar size={12} />
                      {formatDate(p.paymentDate)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Order Summary */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>{t('offlineSales.orderSummary')}</h3>
            <div className={styles.infoGrid}>
              {parseFloat(order.deliveryCharge) > 0 && (
                <div className={styles.infoItem}>
                  <label>{t('offlineSales.deliveryCharge')}</label>
                  <p>+{formatCurrency(order.deliveryCharge)}</p>
                </div>
              )}
              <div className={styles.infoItem}>
                <label>{t('offlineSales.grandTotal')}</label>
                <p><strong>{formatCurrency(order.grandTotal)}</strong></p>
              </div>
              <div className={styles.infoItem}>
                <label>{t('offlineSales.totalPaid')}</label>
                <p>{formatCurrency(order.paidAmount)}</p>
              </div>
              {parseFloat(order.balanceAmount) > 0 && (
                <div className={styles.infoItem}>
                  <label>{t('offlineSales.balanceDue')}</label>
                  <p><strong style={{ color: 'var(--color-error)' }}>{formatCurrency(order.balanceAmount)}</strong></p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelButton}>{t('common.close', 'Close')}</button>
          <div className={styles.actionButtons}>
            {hasPermission('view_offline_sales_orders') && (
              <button onClick={handleViewOrder} className={styles.viewButton}>
                <Eye size={16} /> {t('common.view', 'View')} <ExternalLink size={14} />
              </button>
            )}
            {canEdit && (
              <button onClick={handleEditOrder} className={styles.editButton}>
                <Pencil size={15} /> {t('common.edit', 'Edit Order')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfflineSalesOrderDetailsModal;
